import {
  Box,
  Button,
  Flex,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
} from '@chakra-ui/react';

import { CircularBadge } from '../../CircularBadge';
import { EntityInfoPopover } from '../../EntityInfoPopover';
import type { TNode } from '../../Node';
import { TreeSelection } from '../../TreeSelection';
import type { TreeData } from '../../useTreeApi';
import { searchMatch } from '../../utils/search';
import { VariableLengthSkeleton } from '../../variable-length-skeleton/variable-length-skeleton';
import { useSplitTree } from './useSplitTree';

const TAB_PANEL_PADDING = 8;
const BUTTON_HEIGHT = 24;
const BUTTON_MARGIN = 2;
const CONTENT_HEIGHT_OFFSET = TAB_PANEL_PADDING + BUTTON_HEIGHT + BUTTON_MARGIN;

interface SplitResultsTreeProps {
  companyResults: TreeData[];
  industryResults: TreeData[];
  onCompanyChange: (ids: string[], nodes?: TNode[]) => void;
  onIndustryChange: (ids: string[], nodes?: TNode[]) => void;
  height?: number;
  isLoading?: boolean;
  value?: string[];
  search?: string;
  activeTab?: number;
  onTabChange?: (index: number) => void;
  disableIndustryTab?: boolean;
}

function TreeSkeleton({ height }: { height: number }) {
  return (
    <div style={{ height }}>
      <VariableLengthSkeleton
        count={8}
        fade
        checkboxes
        px={1}
        data-testid="tree-loading-skeleton"
      />
    </div>
  );
}

export const SplitResultsTree = ({
  companyResults,
  industryResults,
  onCompanyChange,
  onIndustryChange,
  height = 400,
  isLoading = false,
  value = [],
  search = '',
  activeTab = 0,
  onTabChange,
  disableIndustryTab = false,
}: SplitResultsTreeProps) => {
  const { companyTreeApi, industryTreeApi, companyCount, industryCount } =
    useSplitTree({
      companyResults,
      industryResults,
      value,
      search,
      onCompanyChange,
      onIndustryChange,
    });

  const adjustedHeight = height - TAB_PANEL_PADDING;
  const contentHeight = adjustedHeight - CONTENT_HEIGHT_OFFSET;

  const handleIndustryExpand = () => {
    if (industryTreeApi.isExpanded) {
      // biome-ignore lint/suspicious/useIterableCallbackReturn: migrated from develop
      industryTreeApi.treeRef.current?.root?.children?.forEach((node) =>
        node.close()
      );
      industryTreeApi.collapse();
    } else {
      // biome-ignore lint/suspicious/useIterableCallbackReturn: migrated from develop
      industryTreeApi.treeRef.current?.root?.children?.forEach((node) =>
        node.open()
      );
      industryTreeApi.expand();
    }
  };

  if (disableIndustryTab) {
    return isLoading ? (
      <TreeSkeleton height={height} />
    ) : (
      <TreeSelection
        data={companyResults}
        treeApi={companyTreeApi}
        height={height}
        searchMatch={(node, search) => {
          if (search.length < 2) {
            return searchMatch(node, search);
          }
          return true;
        }}
        drillMode={{
          isEnabled: true,
          level: 0,
          isRoot: true,
          drillIntoNode: () => null,
          drillOutToLevel: () => null,
          isDrilledInto: () => false,
        }}
        popoverComponent={EntityInfoPopover}
      />
    );
  }

  return (
    <Tabs
      variant="line"
      colorScheme="green"
      size="sm"
      height="100%"
      index={activeTab}
      onChange={onTabChange}
    >
      <TabList display="flex" mb={1}>
        <Tab
          flex="1"
          textAlign="center"
          _selected={{
            opacity: 1,
            color: 'green.500',
            borderColor: 'green.500',
          }}
          opacity={0.6}
          role="tab"
          aria-label="Company"
        >
          <Text fontSize="xs">Company</Text>{' '}
          {companyCount > 0 && (
            <Flex
              data-testid="split-tree-company-tab-badge"
              justifyContent="center"
              flexShrink={0}
              marginLeft="8px"
            >
              <CircularBadge value={companyCount} colorScheme="navyBlue" />
            </Flex>
          )}
        </Tab>
        <Tab
          flex="1"
          textAlign="center"
          _selected={{
            opacity: 1,
            color: 'green.500',
            borderColor: 'green.500',
          }}
          opacity={0.6}
          role="tab"
          aria-label="Industry"
        >
          <Text fontSize="xs">Industry</Text>
          {industryCount > 0 && (
            <Flex
              data-testid="split-tree-industry-tab-badge"
              justifyContent="center"
              flexShrink={0}
              marginLeft="8px"
            >
              <CircularBadge value={industryCount} colorScheme="navyBlue" />
            </Flex>
          )}
        </Tab>
      </TabList>

      <TabPanels>
        <TabPanel p={0}>
          {isLoading ? (
            <TreeSkeleton height={adjustedHeight} />
          ) : (
            <TreeSelection
              data={companyResults}
              treeApi={companyTreeApi}
              height={adjustedHeight}
              searchMatch={(node, search) => {
                if (search.length < 2) {
                  return searchMatch(node, search);
                }
                return true;
              }}
              drillMode={{
                isEnabled: true,
                level: 0,
                isRoot: true,
                drillIntoNode: () => null,
                drillOutToLevel: () => null,
                isDrilledInto: () => false,
              }}
              popoverComponent={EntityInfoPopover}
            />
          )}
        </TabPanel>
        <TabPanel p={0}>
          <Box height={adjustedHeight}>
            {!isLoading && (
              <Button
                variant="subtleLink"
                onClick={handleIndustryExpand}
                mb={BUTTON_MARGIN}
              >
                {industryTreeApi.isExpanded ? 'Collapse All' : 'Expand All'}
              </Button>
            )}
            {isLoading ? (
              <TreeSkeleton height={contentHeight} />
            ) : (
              <TreeSelection
                data={industryResults}
                treeApi={industryTreeApi}
                height={contentHeight}
                openByDefault={false}
                popoverComponent={EntityInfoPopover}
              />
            )}
          </Box>
        </TabPanel>
      </TabPanels>
    </Tabs>
  );
};
